import React from "react";
import Layout from "../../layout/Layout";
import SeoHead from "../../component/SeoHead";
import { pageRouteUrls } from "../../helper/pageRouteUrls";
import {
    CATEGORY_LIVE_TEACHING as data,
    buildFaqSchema,
    PRICING_AS_OF,
    MY_STARTING_PRICE,
    COMPARISONS,
} from "../../data/comparisonData";
import "./marketing.css";

/**
 * Category landing page targeting "cheapest platform to teach live online".
 * Reached at /cheapest-platform-to-teach-live-online.
 */
const CategoryLiveTeaching: React.FC = () => {
    const productSchema: Record<string, unknown> = {
        "@context": "https://schema.org",
        "@type": "Product",
        name: "MyEduLink",
        description: data.metaDescription,
        brand: { "@type": "Brand", name: "MyEduLink" },
        offers: {
            "@type": "Offer",
            price: "19",
            priceCurrency: "USD",
            availability: "https://schema.org/InStock",
            url: "https://myedulink.net/pricing",
        },
    };

    return (
        <Layout>
            <SeoHead
                title={data.title}
                description={data.metaDescription}
                canonicalPath={pageRouteUrls.CheapestLiveTeaching()}
                jsonLd={[buildFaqSchema(data.faqs), productSchema]}
            />

            <div className="mk-page">
                <div className="container">

                    {/* Hero */}
                    <div className="mk-hero">
                        <span className="mk-eyebrow">Live online teaching</span>
                        <h1>The cheapest platform to <span>teach live online</span></h1>
                        <p>{data.intro}</p>
                        <div className="mk-price-badge">
                            <i className="flaticon-badge"></i>
                            <span>Native live classes from {MY_STARTING_PRICE} — 0% platform fee</span>
                        </div>
                        <div className="mk-cta-row">
                            <a className="btn btn-primary btn-hover-dark" href={pageRouteUrls.Register()}>Start free</a>
                            <a className="btn btn-secondary btn-hover-primary" href={pageRouteUrls.Pricing()}>See pricing</a>
                        </div>
                        <div className="mk-reassure">Get paid directly — 2 months free, no credit card, cancel anytime.</div>
                    </div>

                    {/* Price comparison table (cheapest first) */}
                    <div className="mk-table-wrap">
                        <table className="mk-table">
                            <thead>
                                <tr>
                                    <th scope="col">Platform</th>
                                    <th scope="col">Starting price</th>
                                    <th scope="col">Native live classes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.priceTable.map(row => (
                                    <tr key={row.name}>
                                        <td className={row.isMine ? "mk-col-mine mk-row-label" : "mk-row-label"}>{row.name}</td>
                                        <td className={row.isMine ? "mk-col-mine" : ""}>{row.price}</td>
                                        <td className={row.isMine ? "mk-col-mine" : ""}>
                                            {row.nativeLive.startsWith("Yes")
                                                ? <span className="mk-check">✓</span>
                                                : <span className="mk-dash">–</span>}
                                            {row.nativeLive}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <p className="mk-footnote">
                            Competitor starting prices as of {PRICING_AS_OF}; verify against each provider's pricing page before relying on them.
                        </p>
                    </div>

                    {/* Why cheapest without cutting corners */}
                    <div className="mk-section">
                        <h2>Cheapest to start — without losing the essentials</h2>
                        <p style={{ color: "var(--mk-muted)", fontSize: 15, lineHeight: 1.6 }}>
                            A low price only helps if you still get the tools you need to teach and get paid. MyEduLink
                            includes native live classes, auto-graded exams and homework, built-in certificates, and a
                            student marketplace — and charges a 0% platform transaction fee, so you connect your own
                            PayPal and keep what you earn. That's the whole live-teaching stack for {MY_STARTING_PRICE},
                            not a stripped-down starter tier.
                        </p>
                    </div>

                    {/* Deep-link to head-to-head comparisons */}
                    <div className="mk-section">
                        <h2>Compare MyEduLink head-to-head</h2>
                        <div className="mk-cta-row" style={{ justifyContent: "flex-start" }}>
                            {COMPARISONS.map(c => (
                                <a key={c.slug} className="btn btn-secondary btn-hover-primary" href={pageRouteUrls.VsCompetitor(c.slug)}>
                                    vs {c.name}
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* FAQ */}
                    <div className="mk-faq">
                        <h2>Frequently asked questions</h2>
                        {data.faqs.map(faq => (
                            <div className="mk-faq-item" key={faq.q}>
                                <h3>{faq.q}</h3>
                                <p>{faq.a}</p>
                            </div>
                        ))}
                    </div>

                    {/* Final CTA */}
                    <div className="mk-final-cta">
                        <h2>Start teaching live for {MY_STARTING_PRICE}</h2>
                        <p>2 months free, no credit card. Run a live class today and see the difference.</p>
                        <a className="btn btn-primary btn-hover-dark" href={pageRouteUrls.Register()}>Start free</a>
                    </div>

                </div>
            </div>
        </Layout>
    );
};

export default CategoryLiveTeaching;
